import React, { useState, useMemo } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

export function DiscountPopup({
    open,
    onOpenChange,
    items = [],
    currentOrder = {},
    onApplyDiscount,
    discountTypeFlex = null
}) {
    const [selectedDiscountId, setSelectedDiscountId] = useState(null);
    const [description, setDescription] = useState("");
    const [careOf, setCareOf] = useState("");

    const availableDiscounts = useMemo(() => {
        if (!currentOrder?.discountMaster || !Array.isArray(currentOrder.discountMaster)) {
            return [];
        }

        // Group discounts by DiscountId and get unique discount types
        const discountMap = new Map();

        currentOrder.discountMaster.filter(a => a.IsAutoDiscount == false &&
            (a.DiscountTypeFlex == discountTypeFlex || discountTypeFlex == null)
        ).forEach((discount) => {
            if (discount.DiscountId && !discountMap.has(discount.DiscountId)) {
                // Get all product detail IDs that this discount applies to
                const productDetailIds = currentOrder?.discountDetail && Array.isArray(currentOrder.discountDetail)
                    ? currentOrder.discountDetail
                        .filter(d => d.DiscountId === discount.DiscountId)
                        .map(d => d.ProductDetailId)
                    : [];

                discountMap.set(discount.DiscountId, {
                    DiscountId: discount.DiscountId,
                    DiscountPercent: discount.DiscountPercent || 0,
                    DiscountName: discount.DiscountName || `Discount ${discount.DiscountPercent}%`,
                    ProductDetailIds: productDetailIds
                });
            }
        });

        return Array.from(discountMap.values());
    }, [currentOrder, discountTypeFlex]);

    // Find discount for a specific product detail ID
    const findDiscountForProduct = (productDetailId) => {
        if (!currentOrder?.discountDetail) return null;
        return currentOrder.discountDetail.find(
            d => d.ProductDetailId === productDetailId
        );
    };

    const handleApply = () => {

        if (!selectedDiscountId) {
            return;
        }

        const selectedDiscount = availableDiscounts.find(
            d => d.DiscountId === selectedDiscountId
        );

        if (!selectedDiscount) {
            return;
        }

        // Use items from prop, fallback to currentOrder.discountDetail
        const itemsToProcess = items && items.length > 0 ?
            items.filter(a => a.discountPercent == null || a.discountPercent == 0) :
            (currentOrder.discountDetail || []);

        if (!itemsToProcess || itemsToProcess.length === 0) {
            onOpenChange(false);
            return;
        }

        // Get the discount percent from selectedDiscount
        const discountPercent = selectedDiscount.DiscountPercent || 0;
        const productDetailIds = selectedDiscount.ProductDetailIds || [];

        // Check if ProductDetailIds has data or is empty
        const shouldApplyToAllItems = !productDetailIds || productDetailIds.length === 0;

        // Apply discount to items based on ProductDetailIds
        if (onApplyDiscount) {
            const updatedItems = itemsToProcess.map(item => {

                // Get ProductDetailId from item (handle both camelCase and PascalCase)
                const itemProductDetailId = item.ProductDetailId || item.productDetailId;

                // Determine if this item should get the discount
                const shouldApplyDiscount = shouldApplyToAllItems
                    ? true  // Apply to all items if ProductDetailIds is empty
                    : (itemProductDetailId && productDetailIds.includes(itemProductDetailId));  // Apply only if ProductDetailId is in the array

                if (shouldApplyDiscount && discountPercent > 0) {
                    const basePrice = parseFloat(item.basePrice) || parseFloat(item.price) || 0;
                    const itemQuantity = parseInt(item.quantity) || 1;
                    const itemPriceBeforeDiscount = basePrice * itemQuantity;
                    const discountAmount = (itemPriceBeforeDiscount * discountPercent) / 100;

                    return {
                        ...item,
                        discountPercent: discountPercent,
                        discountAmount: discountAmount,
                        discountId: selectedDiscountId,
                        // basePrice: basePrice, // Keep original base price
                        // price: item.price || basePrice // Keep current price
                    };
                }

                // If discount should not be applied to this item, keep it unchanged
                // (don't modify items that are not in the ProductDetailIds array)
                return item;
            });

            onApplyDiscount(updatedItems, selectedDiscountId, careOf, description);
        }

        onOpenChange(false);
    };

    const handleRemoveDiscount = () => {
        // Remove discount from all items
        const itemsToProcess = items && items.length > 0 ? items : (currentOrder.discountDetail || []);

        if (onApplyDiscount && itemsToProcess && itemsToProcess.length > 0) {
            const updatedItems = itemsToProcess.map(item => {
                const basePrice = parseFloat(item.basePrice) || parseFloat(item.price) || 0;
                return {
                    ...item,
                    discountPercent: 0,
                    discountAmount: 0,
                    price: item.price || basePrice, // Keep current price or restore to base
                    basePrice: basePrice,
                    discountId: null,
                };
            });

            onApplyDiscount(updatedItems, null, careOf, description);
        }

        setSelectedDiscountId(null);
        onOpenChange(false);
    };

    const hasActiveDiscount = currentOrder?.discountMaster?.some(item => (item.discountPercent || 0) > 0);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Apply Discount</DialogTitle>
                    <DialogDescription>
                        Select a discount to apply to eligible items in your order.
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4 space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="careOf">Care Of</Label>
                        <Input
                            id="careOf"
                            placeholder="Enter care of information"
                            value={careOf}
                            onChange={(e) => setCareOf(e.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                            id="description"
                            placeholder="Enter description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                        />
                    </div>
                </div>

                <div className="py-4">
                    {availableDiscounts.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[300px] overflow-y-auto pr-2">
                            {availableDiscounts.map((discount) => {
                                const itemsToCheck = items && items.length > 0 ? items : (currentOrder.discountDetail || []);
                                const productDetailIds = discount.ProductDetailIds || [];
                                const shouldShowAllItems = !productDetailIds || productDetailIds.length === 0;

                                const matchingItemsCount = shouldShowAllItems
                                    ? itemsToCheck.length
                                    : itemsToCheck.filter(item => {
                                        const itemProductDetailId = item.ProductDetailId || item.productDetailId;
                                        return itemProductDetailId && productDetailIds.includes(itemProductDetailId);
                                    }).length;

                                const isSelected = selectedDiscountId === discount.DiscountId;

                                return (
                                    <Button
                                        key={discount.DiscountId}
                                        variant={isSelected ? "default" : "outline"}
                                        onClick={() => setSelectedDiscountId(discount.DiscountId)}
                                        className={`h-auto py-4 px-4 flex flex-col items-start justify-center text-left ${isSelected
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "hover:bg-accent"
                                            }`}
                                    >
                                        <div className="flex flex-col w-full">
                                            <span className="font-semibold text-base mb-1">
                                                {discount.DiscountName}
                                            </span>
                                            <span className={`text-sm ${isSelected ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                                                {discount.DiscountPercent}% off
                                            </span>
                                            {matchingItemsCount > 0 && (
                                                <span className={`text-xs mt-1 ${isSelected ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                                                    Applies to {matchingItemsCount} item{matchingItemsCount > 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </div>
                                    </Button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-muted-foreground">
                            <p>No discounts available for this order.</p>
                        </div>
                    )}
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    {hasActiveDiscount && (
                        <Button
                            variant="destructive"
                            onClick={handleRemoveDiscount}
                            className="w-full sm:w-auto"
                        >
                            Remove Discount
                        </Button>
                    )}
                    <div className="flex gap-2 w-full sm:w-auto">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="flex-1 sm:flex-none"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleApply}
                            disabled={!selectedDiscountId}
                            className="flex-1 sm:flex-none"
                        >
                            Apply Discount
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

